import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { useLanguageStore } from '../../stores/languageStore';
import { userApi } from '../../utils/api';
import { User } from '../../types';
import { formatDistanceToNow } from 'date-fns';
import { zhCN, enUS } from 'date-fns/locale';
import toast from 'react-hot-toast';
import { Search, X } from 'lucide-react';
import { getUserGroupColors, PREDEFINED_COLORS } from '../../utils/userGroupHelpers';

const AdminUsers: React.FC = () => {
  const { user, isAuthenticated } = useAuthStore();
  const { language } = useLanguageStore();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<User[] | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [userGroups, setUserGroups] = useState<any[]>([]);
  const isMaster = user?.role === '站长';

  const handleSendVerificationEmail = async (userId: number, email: string) => {
    if (!confirm(`确定要向 ${email} 发送验证邮件吗？`)) return;

    try {
      const response = await userApi.sendVerificationEmail(userId);
      if (response.success) {
        toast.success('验证邮件已发送');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || '发送失败');
    }
  };

  const handleSendResetPasswordEmail = async (userId: number, email: string) => {
    if (!confirm(`确定要向 ${email} 发送重置密码邮件吗？`)) return;

    try {
      const response = await userApi.sendResetPasswordEmail(userId);
      if (response.success) {
        toast.success('重置密码邮件已发送');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || '发送失败');
    }
  };

  const handleSetEmailVerified = async (userId: number, emailVerified: boolean, email: string) => {
    const action = emailVerified ? '标记为未验证' : '标记为已验证';
    if (!confirm(`确定要将 ${email} ${action} 吗？`)) return;

    try {
      const response = await userApi.setEmailVerified(userId, !emailVerified);
      if (response.success) {
        toast.success(response.message);
        if (searchQuery) {
          searchUsers(searchQuery);
        } else {
          fetchUsers();
        }
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || '操作失败');
    }
  };

  useEffect(() => {
    if (user?.role !== '管理员' && user?.role !== '站长' && user?.role !== '超级管理员') {
      return;
    }
    fetchUsers();
    loadUserGroups();
  }, [page, user]);

  const loadUserGroups = async () => {
    try {
      const response = await userApi.getUserGroups();
      if (response.success) {
        let groups = response.data || [];
        if (!isMaster) {
          groups = groups.filter(g => g.role !== '超级管理员');
        }
        setUserGroups(groups);
      }
    } catch (error) {
      console.error('Failed to load user groups:', error);
    }
  };

  useEffect(() => {
    if (searchQuery.trim()) {
      const debounceTimer = setTimeout(() => {
        searchUsers(searchQuery);
      }, 300);
      return () => clearTimeout(debounceTimer);
    } else {
      setSearchResults(null);
    }
  }, [searchQuery]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await userApi.getUserList({ page, pageSize: 20 });
      if (response.success) {
        setUsers(response.data || []);
        setTotalPages(response.pagination.totalPages);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const searchUsers = async (query: string) => {
    if (!query.trim()) return;
    try {
      setSearchLoading(true);
      const response = await userApi.searchUser(query);
      if (response.success && response.data) {
        setSearchResults(response.data.users || []);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Search users failed:', error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleBan = async (userId: number) => {
    if (!confirm('确定要封禁该用户吗？')) return;

    try {
      const response = await userApi.banUser(userId);
      if (response.success) {
        toast.success('封禁成功');
        if (searchQuery) {
          searchUsers(searchQuery);
        } else {
          fetchUsers();
        }
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || '操作失败');
    }
  };

  const handleUnban = async (userId: number) => {
    if (!confirm('确定要解封该用户吗？')) return;

    try {
      const response = await userApi.unbanUser(userId);
      if (response.success) {
        toast.success('解封成功');
        if (searchQuery) {
          searchUsers(searchQuery);
        } else {
          fetchUsers();
        }
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || '操作失败');
    }
  };

  const handleUserGroupChange = async (userId: number, userGroupId: number | null) => {
    try {
      await userApi.setUserGroup(userId, userGroupId);
      
      if (userGroupId) {
        const group = userGroups.find(g => g.id === userGroupId);
        if (group && group.role) {
          await userApi.updateUserRole(userId, group.role);
        }
      }
      
      toast.success('用户组更新成功');
      if (searchQuery) {
        searchUsers(searchQuery);
      } else {
        fetchUsers();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || '用户组更新失败');
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
  };

  const formatDate = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true, locale: language === 'zh' ? zhCN : enUS });
    } catch {
      return dateString;
    }
  };

  const getRoleClass = (role: string) => {
    switch (role) {
      case '站长':
        return 'bg-yellow-100 text-yellow-700';
      case '超级管理员':
        return 'bg-purple-100 text-purple-700';
      case '管理员':
        return 'bg-orange-100 text-orange-700';
      case '频道主':
        return 'bg-pink-100 text-pink-700';
      case '志愿者':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getNicknameColor = (role: string) => {
    switch (role) {
      case '频道主':
        return 'text-pink-600';
      case '超级管理员':
        return 'text-purple-600';
      case '站长':
        return 'text-yellow-600';
      case '志愿者':
        return 'text-red-600';
      default:
        return 'text-gray-900';
    }
  };

  const displayUsers = searchResults !== null ? searchResults : users;

  // 获取用户组信息和颜色
  const getUserGroupInfo = (userGroupId: number | null) => {
    return getUserGroupColors(userGroups, userGroupId);
  };

  if (!isAuthenticated || (user?.role !== '管理员' && user?.role !== '站长' && user?.role !== '超级管理员')) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-gray-900">{'用户管理'}</h1>

      <div className="card p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={'搜索用户ID、用户名、昵称、邮箱...'}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            {searchQuery && (
              <button
                onClick={clearSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {searchQuery && (
            <div className="text-sm text-gray-500 flex items-center">
              {searchLoading ? ('搜索中...') : `${'找到'} ${searchResults?.length || 0} ${'个结果'}`}
            </div>
          )}
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                  ID
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {'用户'}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {'邮箱'}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                  {'角色'}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {'用户组'}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                  {'状态'}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                  {'邮箱验证'}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                  {'注册时间'}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
                  {'操作'}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                    {'加载中...'}
                  </td>
                </tr>
              ) : displayUsers.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                    {searchQuery ? ('未找到匹配的用户') : ('暂无用户数据')}
                  </td>
                </tr>
              ) : (
                displayUsers.map((u) => (
                  <tr key={u.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {u.id}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <img
                          src={u.avatar || `https://ui-avatars.com/api/?name=${u.nickname || u.username || 'User'}&background=3b82f6&color=fff`}
                          alt={u.nickname || u.username}
                          className="w-8 h-8 rounded-full flex-shrink-0"
                        />
                        <div className="ml-3 min-w-0">
                          <div className="flex items-center">
                            {/* 显示用户组角色（如果有） */}
                            {getUserGroupInfo(u.user_group_id).role && (
                              <span
                                className="text-xs px-1.5 py-0.5 rounded mr-2"
                                style={{
                                  backgroundColor: getUserGroupInfo(u.user_group_id).badgeColor + '20',
                                  color: getUserGroupInfo(u.user_group_id).badgeColor,
                                  border: `1px solid ${getUserGroupInfo(u.user_group_id).badgeColor}40`
                                }}
                              >
                                {getUserGroupInfo(u.user_group_id).role}
                              </span>
                            )}
                            <div
                              className="text-sm font-medium truncate"
                              style={{ color: getUserGroupInfo(u.user_group_id).nicknameColor }}
                            >
                              {u.nickname}
                            </div>
                          </div>
                          <div className="text-xs text-gray-500 truncate">@{u.username}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 truncate max-w-[150px]">
                      {u.email}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getRoleClass(u.role)}`}
                      >
                        {u.role}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {u.role !== '站长' ? (
                        <select
                          value={u.user_group_id ?? ''}
                          onChange={(e) => {
                            const groupId = e.target.value === '' ? null : parseInt(e.target.value);
                            handleUserGroupChange(u.id, groupId);
                          }}
                          className="px-2 py-1 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        >
                          <option value="">{'无用户组'}</option>
                          {userGroups.map((group) => (
                            <option key={group.id} value={group.id}>
                              {group.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className="text-sm text-gray-500">{'站长'}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          u.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {u.status === 'active' ? ('正常') : ('封禁')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                      {formatDate(u.created_at)}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {u.role !== '站长' && (
                        u.status === 'active' ? (
                          <button
                            onClick={() => handleBan(u.id)}
                            className="text-red-600 hover:text-red-900 text-sm font-medium"
                          >
                            {'封禁'}
                          </button>
                        ) : (
                          <button
                            onClick={() => handleUnban(u.id)}
                            className="text-green-600 hover:text-green-900 text-sm font-medium"
                          >
                            {'解封'}
                          </button>
                        )
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!searchQuery && totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 p-4 border-t border-gray-200">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {'上一页'}
            </button>
            <span className="text-sm text-gray-600">
              {'第'} {page} / {totalPages} {'页'}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {'下一页'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsers;
